import {useState} from 'react';
import type {FC} from 'react';
import {
  Alert,
  Box,
  Button,
  DialogActions,
  DialogContent,
  MultiSelect,
  Stack,
  TextField,
  Typography,
} from '@wellstone-solutions/web';
import type {UIResourceType} from '@wellstone-solutions/common/models/rest';
import type {FieldsEnum} from 'api/v2';
import {isAlreadyFlaggedError, useFlagResource} from '../../queries';
import {FLAG_FIELD_OPTIONS} from './flagFields';
import {FlagResourceSummary} from './FlagResourceSummary';

// "already flagged" means someone else got there first; the resource shows
// "Flag sent" either way.
export type FlagOutcome = 'sent' | 'alreadyFlagged';

interface FlagResourceViewProps {
  resource: UIResourceType;
  onCancel: () => void;
  onFlagged: (outcome: FlagOutcome) => void;
}

// The flag form, shown as a view inside ResourceModal.
export const FlagResourceView: FC<FlagResourceViewProps> = ({
  resource,
  onCancel,
  onFlagged,
}) => {
  const [fields, setFields] = useState<FieldsEnum[]>([]);
  const [description, setDescription] = useState('');
  const [hasError, setHasError] = useState(false);
  const {mutateAsync, isPending} = useFlagResource();

  const trimmedDescription = description.trim();
  const isComplete = fields.length > 0 && trimmedDescription.length > 0;

  // mutateAsync, not mutate's per-call callbacks: those are skipped if this
  // view unmounts mid-request (e.g. the header's back button), and the modal
  // would never hear how the flag went.
  const handleSubmit = async () => {
    setHasError(false);
    try {
      await mutateAsync({
        resourceId: resource.id,
        fields,
        description: trimmedDescription,
      });
      onFlagged('sent');
    } catch (error) {
      if (isAlreadyFlaggedError(error)) {
        onFlagged('alreadyFlagged');
        return;
      }
      // Stay on the form so nothing typed is lost.
      setHasError(true);
    }
  };

  return (
    <>
      <DialogContent dividers>
        <Stack direction={{xs: 'column', md: 'row'}} spacing={{xs: 3, md: 4}}>
          <Box sx={styles.summaryColumn}>
            <FlagResourceSummary resource={resource} />
          </Box>
          <Stack spacing={2.5} sx={styles.formColumn}>
            <Typography>
              Flagging tells us a detail is wrong or missing.
            </Typography>
            <MultiSelect
              fieldName="flag-fields"
              label="Select what's wrong"
              items={FLAG_FIELD_OPTIONS}
              value={fields}
              onChange={(values) => setFields(values as FieldsEnum[])}
              visibleItemCount={6}
            />
            <TextField
              fullWidth
              multiline
              required
              minRows={5}
              label="Description"
              helperText="Include the correct details if you know them"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
            />
            {hasError && (
              <Alert severity="error">Couldn't send the flag. Try again.</Alert>
            )}
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions sx={styles.actions}>
        <Button variant="text" onClick={onCancel} disabled={isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isPending || !isComplete}>
          Send report
        </Button>
      </DialogActions>
    </>
  );
};

const styles = {
  summaryColumn: {
    flex: 1,
    minWidth: 0,
    maxHeight: {md: 420},
    overflowY: {md: 'auto'},
  },
  formColumn: {flex: 1, minWidth: 0},
  actions: {px: 3, py: 1.75},
};
